import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, stringToBoolean } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import {
  Page,
  NamedLink,
  NamedRedirect,
  IconEmailSent,
  InlineTextButton,
  IconClose,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  Modal,
} from '../../components';
import { LoginForm, SignupForm } from '../../forms';
import { TopbarContainer } from '../../containers';
import { login, authenticationInProgress, signup, setPreviousPage } from '../../ducks/Auth.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { loadData } from './AuthenticationPage.duck';
import BlogContent from '../../components/BlogContent';
import { pathByRouteName } from '../../util/routes';

import css from './AuthenticationPage.css';

export const AuthenticationPageComponent = (props) => {
  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    history,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    tab,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    setPreviousPage,
    previousPage,
    strapiData,
  } = props;
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const isLogin = tab === 'login';
  const from = location.state && location.state.from ? location.state.from : null;
  const canGoBack = location?.state?.canGoBack;
  useEffect(() => {
    if (!from) return;

    setPreviousPage(from);
  }, [from]);
  useEffect(() => {
    if (!isAuthenticated || !canGoBack) return;

    history.goBack();
  }, [isAuthenticated, canGoBack]);

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  console.log('previousPage', previousPage);
  const isPreviousPageYourAvailabilities =
    previousPage && /^\/your-availabilities(\?.*)?$/.test(previousPage);
  // We only want to show the email verification dialog in the signup
  // tab if the user isn't being redirected somewhere else
  // (i.e. `from` is present). We must also check the `emailVerified`
  // flag only when the current user is fully loaded.
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;
  // Already authenticated, redirect away from auth page
  if (isAuthenticated && previousPage && !canGoBack) {
    return <Redirect to={previousPage} />;
  } else if (isAuthenticated && currentUserLoaded && !canGoBack && !showEmailVerification) {
    return <NamedRedirect name="LandingPage" />;
  }

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  // eslint-disable-next-line no-confusing-arrow
  const errorMessage = (error, message) => (error ? message : null);
  const loginOrSignupError = isLogin
    ? errorMessage(loginError, loginErrorMessage)
    : errorMessage(signupError, signupErrorMessage);

  const handleSubmitSignup = (values = {}) => {
    const { fname, lname, isPoolOwner, ...rest } = values;
    if (!values.password || !fname || !lname || !values.email) {
      return;
    }
    const isPoolOwnerAsBoolean = stringToBoolean(isPoolOwner);

    const params = {
      firstName: fname.trim(),
      lastName: lname.trim(),
      isPoolOwner: isPoolOwnerAsBoolean,
      privateData: { language: config.locale, country: config.custom.country },
      ...rest,
    };
    // Après inscription, rediriger vers la page de brouillon d'annonce (NewListingPage)
    const redirectToDraft = () => {
      const newListingPath = pathByRouteName('NewListingPage', props.routeConfiguration || [], {});
      if (newListingPath) {
        history.push(newListingPath);
      } else {
        // Fallback raisonnable : URL statique connue
        history.push('/l/new');
      }
    };
    submitSignup(params, redirectToDraft);
  };

  const formTitle = isLogin ? (
    intl.formatMessage({ id: 'AuthenticationPage.loginTitle' })
  ) : (
    <FormattedMessage
      id="AuthenticationPage.signupTitle"
      values={{ swimmy: <span className={css.swimmy}>Swimmy</span> }}
    />
  );

  const formSubtitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.loginSubtitle' })
    : intl.formatMessage({ id: 'AuthenticationPage.signupSubtitle' });

  const formContent = (
    <div className={css.content}>
      <div className={css.formTopContainer}>
        {isLogin ? <div className={css.childImg} /> : <div className={css.raphImg} />}
        <h1 className={css.formTitle}>{formTitle}</h1>
        <span className={css.formSubtitle}>{formSubtitle}</span>
      </div>
      {isPreviousPageYourAvailabilities && (
        <div className={css.cautionMessage}>
          <strong>
            <FormattedMessage id="AvailabilitiesPage.loginRequiredTitle" />
          </strong>
          <br />
          <FormattedMessage id="AvailabilitiesPage.loginRequiredDesc" />
        </div>
      )}
      {loginOrSignupError}
      {isLogin ? (
        <LoginForm className={css.form} onSubmit={submitLogin} inProgress={authInProgress} />
      ) : (
        <SignupForm
          canGoBack={canGoBack}
          className={css.form}
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          onOpenTermsOfService={() => setTosModalOpen(true)}
          previousPage={previousPage}
        />
      )}
      {isLogin ? <div className={css.manImg} /> : null}
    </div>
  );

  const name = user.attributes.profile.firstName;
  const email = <span className={css.email}>{user.attributes.email}</span>;

  const resendEmailLink = (
    <InlineTextButton className={css.modalHelperLink} onClick={onResendVerificationEmail}>
      <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
    </InlineTextButton>
  );
  const fixEmailLink = (
    <NamedLink className={css.modalHelperLink} name="AccountPersonalInfosPage">
      <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
    </NamedLink>
  );

  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';
  const resendErrorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  const emailVerificationContent = (
    <div className={css.content}>
      <NamedLink className={css.verifyClose} name="AccountPersonalInfosPage">
        <span className={css.closeText}>
          <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </NamedLink>
      <IconEmailSent className={css.modalIcon} />
      <h1 className={css.modalTitle}>
        <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
      </h1>
      <p className={css.modalMessage}>
        <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
      </p>
      {resendErrorMessage}

      <div className={css.bottomWrapper}>
        <p className={css.modalHelperText}>
          {sendVerificationEmailInProgress ? (
            <FormattedMessage id="AuthenticationPage.sendingEmail" />
          ) : (
            <FormattedMessage id="AuthenticationPage.resendEmail" values={{ resendEmailLink }} />
          )}
        </p>
        <p className={css.modalHelperText}>
          <FormattedMessage id="AuthenticationPage.fixEmail" values={{ fixEmailLink }} />
        </p>
      </div>
    </div>
  );

  const siteTitle = config.siteTitle;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

  const topbarClasses = classNames({
    [css.hideOnMobile]: showEmailVerification,
  });

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer className={topbarClasses} currentPage="AuthenticationPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.root}>
            {showEmailVerification ? emailVerificationContent : formContent}
          </div>
          <Modal
            id="AuthenticationPage.tos"
            isOpen={tosModalOpen}
            onClose={() => setTosModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.termsWrapper}>
              <BlogContent {...strapiData} />
            </div>
          </Modal>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
};

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
  const { isAuthenticated, loginError, signupError, previousPage } = state.Auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const { data } = state.BlogPage;

  return {
    previousPage,
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    strapiData: data,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setPreviousPage: (previousPage) => dispatch(setPreviousPage(previousPage)),
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: (params, goBack) => dispatch(signup(params, goBack)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(AuthenticationPageComponent);

AuthenticationPage.loadData = () => loadData();

export default AuthenticationPage;
